import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Mail, Check, Loader2 } from 'lucide-react';
import { trackNewsletterSignup } from '../utils/analytics';

const API_URL = "";

const PROGRAM_INTERESTS = [
  { value: 'kids_classes', label: 'Kids Classes' },
  { value: 'adult_classes', label: 'Adult Classes' },
  { value: 'workshops', label: 'Workshops' },
  { value: 'events', label: 'Events & Exhibitions' },
];

/**
 * Newsletter Signup Form
 * Public form for website visitors to subscribe to the newsletter
 */
const NewsletterSignupForm = ({ compact = false, className = '' }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    program_interest: []
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      program_interest: prev.program_interest.includes(interest)
        ? prev.program_interest.filter(i => i !== interest)
        : [...prev.program_interest, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          newsletter_opt_in: true,
          opt_in_source: 'website_form',
          member_type: 'general'
        })
      });

      if (response.ok) {
        setSuccess(true);
        toast.success('Thank you for subscribing!');
        // Track newsletter signup conversion
        trackNewsletterSignup(formData.email, formData.program_interest);
        setFormData({ email: '', first_name: '', last_name: '', program_interest: [] });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast.error('Error subscribing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-6 text-center ${className}`}>
        <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="font-semibold text-green-900 mb-2">You're Subscribed!</h3>
        <p className="text-sm text-green-700">
          Thank you for joining our newsletter. We'll keep you updated on the latest from CAFA.
        </p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => setSuccess(false)}
        >
          Subscribe Another Email
        </Button>
      </div>
    );
  }

  // Compact version for footer
  if (compact) {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
          required
        />
        <Button 
          type="submit" 
          disabled={loading}
          className="bg-white text-stone-900 hover:bg-stone-100"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    );
  }

  // Full form version
  return (
    <div className={`bg-white border border-stone-200 rounded-xl p-6 shadow-sm ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-cafa-red/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-cafa-red" />
        </div>
        <div>
          <h3 className="font-semibold text-stone-900">Subscribe to Our Newsletter</h3>
          <p className="text-sm text-stone-500">Get updates on classes, workshops & events</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="text-sm">Email Address *</Label>
          <Input
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="mt-1"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">First Name</Label>
            <Input
              placeholder="First name"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm">Last Name</Label>
            <Input
              placeholder="Last name"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm mb-2 block">I'm interested in (select all that apply)</Label>
          <div className="flex flex-wrap gap-2">
            {PROGRAM_INTERESTS.map(interest => (
              <button
                key={interest.value}
                type="button"
                onClick={() => toggleInterest(interest.value)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  formData.program_interest.includes(interest.value)
                    ? 'bg-cafa-red text-white border-cafa-red'
                    : 'bg-white text-stone-600 border-stone-300 hover:border-cafa-red'
                }`}
              >
                {interest.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-stone-500">
          By subscribing, you consent to receive marketing emails from The Coppini Academy of Fine Art. 
          You can unsubscribe at any time.
        </p>

        <Button 
          type="submit" 
          disabled={loading}
          className="w-full bg-cafa-red hover:bg-cafa-red-900"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Subscribing...</>
          ) : (
            <><Mail className="w-4 h-4 mr-2" /> Subscribe to Newsletter</>
          )}
        </Button>
      </form>
    </div>
  );
};

export default NewsletterSignupForm;
